import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Leaf, ArrowRight, Sparkles, ShoppingCart } from "lucide-react";
import Footer from "../components/Footer";
import NavBar from "../components/Navbar/NavBar";
import { Helmet } from "react-helmet";

import organicBottle from "../images/Organic.jpg";
import organic2 from "../images/organic2.jpg";
import metal from "../images/metal.jpg";

import flavoredBottle from "../images/extravirgin.jpg";
import extravirgin2 from "../images/extravirgin2.jpg";

import bg2 from "../images/bg2.svg";

import extraBottle from "../images/Flavored.jpg";

import flavored2 from "../images/flavored2.jpg";

import hero1 from "../images/olive4.jpg";
import hero2 from "../images/olive3.jpg";
import { Link, useNavigate } from "react-router-dom";
import ProductDetailModal from "./ProductDetailModal";

const productCards = [
  {
    id: 1,
    title: "Extra Virgin Olive Oil",
    description:
      "Cold-pressed from our finest olives, rich in flavor and aroma.",
    highlights: ["First cold press", "Low acidity (<0.8%)", "Peppery finish"],
    image: flavoredBottle,
    image2: extravirgin2,
    details: [
      { k: "Varietals", v: "Chemlali, Chetoui" },
      { k: "Harvest", v: "Early to mid-season" },
      { k: "Origin", v: "Kairouan, Tunisia" },
      { k: "Acidity", v: "< 0.8%" },
      { k: "Formats", v: " 250ml /500ml / 750ml / 1L / 3L /  5L / 10L / 20 L" },
    ],
    fullDescription:
      "Our Extra Virgin Olive Oil is the pinnacle of quality, extracted from carefully selected olives using traditional cold-pressing methods. This premium oil delivers an exceptional balance of fruity notes with a distinctive peppery finish that enhances any culinary creation.",
    price: { "250ml": 24.99, "500ml": 39.99, "750ml": 54.99 },
    rating: 4.8,
    reviews: 127,
    certifications: ["EU PDO", "International Olive Council"],
    nutritionalInfo: {
      calories: "884 per 100ml",
      fat: "100g",
      saturatedFat: "14g",
      monounsaturated: "73g",
      polyunsaturated: "11g",
    },
    storage:
      "Store in a cool, dark place. Best consumed within 24 months of harvest.",
    uses: ["Salads", "Dipping", "Cooking", "Finishing"],
    awards: [
      "Gold Medal - NYIOOC 2023",
      "Best in Class - International Olive Oil Competition",
    ],
  },
  {
    id: 2,
    title: "Organic Olive Oil",
    description: "Certified organic oil maintaining purity and natural taste.",
    highlights: [
      "EU Organic Certified",
      "No pesticides",
      "Sustainable farming",
    ],
    image: organicBottle,
    image2: organic2,
    details: [
      { k: "Certification", v: "EU, USDA Organic" },
      { k: "Farming", v: "Regenerative, pesticide-free" },
      { k: "Origin", v: "Family-owned groves" },
      { k: "Acidity", v: "< 0.8%" },
      { k: "Formats", v: " 250ml /500ml / 750ml / 1L / 3L /  5L / 10L / 20 L" },
    ],
    fullDescription:
      "Our Flavored Olive Oil collection transforms the traditional olive oil experience with carefully crafted natural infusions. Each bottle combines our premium extra virgin base with fresh herbs and spices to create distinctive flavors perfect for culinary experimentation.",
    price: { "250ml": 29.99, "500ml": 49.99 },
    rating: 4.9,
    reviews: 89,
    certifications: ["EU Organic", "USDA Organic", "Soil Association"],
    nutritionalInfo: {
      calories: "884 per 100ml",
      fat: "100g",
      saturatedFat: "14g",
      monounsaturated: "73g",
      polyunsaturated: "11g",
    },
    storage:
      "Store in a cool, dark place. Best consumed within 24 months of harvest.",
    uses: [
      "Raw consumption",
      "Light cooking",
      "Salad dressings",
      "Health supplements",
    ],
    awards: ["Organic Excellence Award", "Sustainable Farming Recognition"],
  },
  {
    id: 3,
    title: "Flavored Olive Oil",
    description: "Infused with herbs and spices for culinary excellence.",
    highlights: ["Natural infusions", "Perfect for dressings"],
    image: extraBottle,
    image2: flavored2,
    details: [
      { k: "Infusions", v: "Lemon, Garlic, Red Pepper, Orange" },
      { k: "Base", v: "Premium EVOO" },
      { k: "Use", v: "Salads, seafood, bread" },
      { k: "Acidity", v: "< 0.8%" },
      { k: "Formats", v: " 250ml /500ml / 750ml / 1L / 3L /  5L / 10L / 20 L" },
    ],
    fullDescription:
      "Our Organic Olive Oil represents our commitment to sustainable agriculture and environmental stewardship. Grown without synthetic pesticides or fertilizers, this oil maintains the pure essence of the olive while supporting regenerative farming practices.",

    price: { "250ml": 32.99 },
    rating: 4.7,
    reviews: 156,
    certifications: ["Natural Ingredients", "No Artificial Additives"],
    nutritionalInfo: {
      calories: "884 per 100ml",
      fat: "100g",
      saturatedFat: "14g",
      monounsaturated: "73g",
      polyunsaturated: "11g",
    },
    storage:
      "Store in a cool, dark place. Best consumed within 24 months of opening.",
    uses: ["Marinades", "Dressings", "Bread dipping", "Grilled vegetables"],
    awards: ["Innovation in Flavor Award", "Chef's Choice Recognition"],
  },
];

const heroImages = [hero1, hero2];

const Products = () => {
  const navigate = useNavigate();
  const [heroIndex, setHeroIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSize, setSelectedSize] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [productImageIndexes, setProductImageIndexes] = useState({});
  const [isVisible, setIsVisible] = useState(false);

  const heroRefs = useRef(heroImages.map(() => React.createRef()));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  // Preload images
  useEffect(() => {
    const currentScrollPos = window.pageYOffset;

    setIsVisible(currentScrollPos > 100);

    heroImages.forEach((src, i) => {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        setLoadedImages((prev) => new Set(prev).add(i));
      };
    });
  }, []);

  // Carousel auto-advance
  useEffect(() => {
    const id = setInterval(() => {
      setHeroIndex((i) => (i + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(id);
  }, []);

  // Modal functions
  const openModal = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
    setSelectedSize(Object.keys(product.price || {})[0] || "");
    setQuantity(1);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
    setSelectedSize("");
    setQuantity(1);
    document.body.style.overflow = "unset";
  };

  const handleQuantityChange = (change) => {
    setQuantity((prev) => Math.max(1, prev + change));
  };

  const handleAddToCart = () => {
    if (!selectedSize) return;
    console.log(
      `Added ${quantity} x ${selectedProduct.title} (${selectedSize}) to cart`
    );
    closeModal();
  };

  const handleBuyNow = (product) => {
    // Get default size (first available size)
    const defaultSize = Object.keys(product.price || {})[0] || "";
    if (!defaultSize) {
      // If no size available, open modal for size selection
      openModal(product);
      return;
    }
    
    // Navigate to checkout with product data
    navigate("/checkout", {
      state: {
        product: {
          ...product,
          size: defaultSize,
          quantity: 1,
        },
      },
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: selectedProduct.title,
        text: selectedProduct.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Product link copied to clipboard!");
    }
  };

  return (
    <motion.div
      className="min-h-screen bg-[#fff6e3] text-black overflow-x-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <NavBar />
      <Helmet>
        <title>Solivera Products - Premium Tunisian Olive Oils</title>
        <meta
          name="description"
          content="Explore Solivera's range of premium extra virgin, organic, and flavored olive oils. High-quality Tunisian olive oils for culinary excellence."
        />
        <meta
          property="og:title"
          content="Solivera Olive Oils - Premium Products"
        />
        <meta
          property="og:description"
          content="Discover our selection of Extra Virgin, Organic, and Flavored Olive Oils from Tunisia. Quality oils for chefs, distributors, and health-conscious consumers."
        />
        <meta property="og:image" content={hero1} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      {/* Hero Banner */}
      <section className="relative pt-32 pb-0 md:pt-40 lg:pt-48 overflow-hidden min-h-[80vh] md:min-h-[90vh]">
        <div className="absolute inset-0 z-0">
          {/* Carousel */}
          {heroImages.map((src, i) => (
            <AnimatePresence key={i}>
              {i === heroIndex && loadedImages.has(i) && (
                <motion.img
                  key={i}
                  src={src}
                  alt="Olive grove"
                  className="absolute inset-0 w-full h-full object-cover"
                  initial={{ opacity: 0, scale: 1.2 }}
                  animate={{ opacity: 1, scale: 1.1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.5, ease: [0.6, -0.05, 0.01, 0.99] }}
                />
              )}
            </AnimatePresence>
          ))}
          <div className="absolute inset-0 bg-gradient-to-br from-[#272e02]/30 via-transparent to-[#225601]/20" />
        </div>

        <div
          key={heroIndex}
          className="absolute bottom-10 left-8 md:left-16 max-w-md text-white z-20"
          style={{ fontFamily: "Inter18Light" }}
        >
          <h1
            className="text-3xl md:text-5xl font-bold leading-snug drop-shadow-lg"
            style={{ fontFamily: "Nephilm" }}
          >
            {heroIndex === 0 && "Nature's Finest Olive Oil"}
            {heroIndex === 1 && "Sustainably Harvested Groves"}
          </h1>
          <p className="mt-3 text-base md:text-xl text-white/85 drop-shadow-md">
            {heroIndex === 0 &&
              "Experience the rich aroma of cold-pressed extra virgin olive oil."}
            {heroIndex === 1 &&
              "Our organic groves are cared for using sustainable farming methods."}
          </p>
        </div>

        {/* Navigation dots */}
        <motion.div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex items-center justify-center gap-3">
          {heroImages.map((_, i) => (
            <motion.button
              key={i}
              onClick={() => setHeroIndex(i)}
              className={`relative w-3 h-3 rounded-full transition-all duration-300 ${
                i === heroIndex
                  ? "bg-[#225601] scale-125"
                  : "bg-white/60 hover:bg-white/80"
              }`}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              aria-label={`Slide ${i + 1}`}
            >
              {i === heroIndex && (
                <motion.div
                  className="absolute inset-0 rounded-full bg-[#225601]/30"
                  initial={{ scale: 0 }}
                  animate={{ scale: 2 }}
                  transition={{ duration: 0.6 }}
                />
              )}
            </motion.button>
          ))}
        </motion.div>
      </section>

      {/* Products Section */}
      <section className="pt-0 pb-0">
        <div className="w-full">
          {productCards.map((product, index) => (
            <div key={product.title} className="mb-0 last:mb-0">
              <div
                className={`grid grid-cols-1 lg:grid-cols-2 min-h-[450px] overflow-hidden shadow-xl ${
                  index % 2 === 1 ? "lg:grid-flow-col-dense" : ""
                }`}
              >
                {/* Product Image with Carousel */}
                <motion.div
                  className={`relative ${
                    index % 2 === 1 ? "lg:col-start-2" : ""
                  }`}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-[#2c3e50] via-[#34495e] to-[#8b4513]"></div>
                  <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-[#8b4513]/20 to-[#a0522d]/40"></div>
                  <div className="relative h-full overflow-hidden">
                    <AnimatePresence mode="wait">
                      <motion.img
                        key={`${product.id}-${
                          productImageIndexes[product.id] || 0
                        }`}
                        src={
                          productImageIndexes[product.id] === 1
                            ? product.image2
                            : product.image
                        }
                        alt={`${product.title} ${
                          productImageIndexes[product.id] === 1
                            ? "lifestyle"
                            : "product"
                        }`}
                        className="w-full h-full object-cover"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.05 }}
                        transition={{ duration: 0.5 }}
                      />
                    </AnimatePresence>

                    {/* Image Navigation Dots */}
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10 flex gap-2">
                      {[0, 1].map((imgIdx) => (
                        <button
                          key={imgIdx}
                          onClick={() =>
                            setProductImageIndexes((prev) => ({
                              ...prev,
                              [product.id]: imgIdx,
                            }))
                          }
                          className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            (productImageIndexes[product.id] || 0) === imgIdx
                              ? "bg-[#d4a574] w-6"
                              : "bg-white/60 hover:bg-white/80"
                          }`}
                          aria-label={`View image ${imgIdx + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                </motion.div>

                {/* Product Info */}
                <motion.div
                  className={`relative ${
                    index % 2 === 1 ? "lg:col-start-1" : ""
                  }`}
                  initial={{ opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                >
                  <div
                    className="h-full bg-gradient-to-br from-[#8b7355] to-[#6b5b47] p-8 flex flex-col justify-center"
                    style={{ fontFamily: "Inter" }}
                  >
                    <div className="w-16 h-0.5 bg-[#d4a574] mb-6"></div>
                    <motion.h2
                      className="text-2xl md:text-3xl font-bold text-[#d4a574] mb-4 leading-tight"
                      style={{ fontFamily: "Nephilm" }}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.4, duration: 0.6 }}
                    >
                      {product.title}
                    </motion.h2>
                    <motion.p
                      className="text-white/90 text-base leading-relaxed mb-6"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5, duration: 0.6 }}
                    >
                      {product.description}
                    </motion.p>
                    <motion.ul
                      className="space-y-2 mb-6"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.6, duration: 0.6 }}
                    >
                      {product.highlights.map((highlight, i) => (
                        <motion.li
                          key={highlight}
                          className="flex items-center text-white/80"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.7 + i * 0.1, duration: 0.4 }}
                        >
                          <div className="w-2 h-2 bg-[#d4a574] rounded-full mr-3"></div>
                          {highlight}
                        </motion.li>
                      ))}
                    </motion.ul>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                      {product.details.map((detail) => (
                        <div
                          key={detail.k}
                          className="bg-white/10 rounded-lg p-2 backdrop-blur-sm border border-white/20"
                        >
                          <div className="text-[#d4a574] text-sm font-semibold mb-1">
                            {detail.k}
                          </div>
                          <div className="text-white/80 text-sm">
                            {detail.v}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={() => handleBuyNow(product)}
                        className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white font-semibold px-6 py-3 rounded-full transition-all duration-300 ease-in-out shadow-lg hover:shadow-xl"
                      >
                        <ShoppingCart className="w-5 h-5" />
                        Buy Now
                        <ArrowRight className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => openModal(product)}
                        className="flex-1 inline-flex items-center justify-center gap-2 bg-[#d4a574] hover:bg-[#d4a574]/80 text-white font-semibold px-6 py-3 rounded-full transition-all duration-300 ease-in-out shadow-lg hover:shadow-xl"
                      >
                        View Details
                        <ArrowRight className="w-5 h-5" />
                      </button>
                      <Link
                        to="/contact"
                        className="inline-flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 text-white font-semibold px-6 py-3 rounded-full transition-all duration-300 ease-in-out backdrop-blur-sm border border-white/30"
                      >
                        Contact
                      </Link>
                    </div>
                    <motion.div
                      className="absolute top-6 right-6 w-6 h-6 text-[#d4a574] opacity-60"
                      initial={{ opacity: 0, rotate: 0 }}
                      whileInView={{ opacity: 0.6, rotate: 360 }}
                      viewport={{ once: true }}
                      transition={{ delay: 1.2, duration: 2 }}
                    >
                      <Sparkles className="w-full h-full" />
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="relative w-full min-h-[70vh] md:min-h-[85vh] overflow-hidden flex items-end">
        <img
          src={bg2}
          alt="Bulk Packaging Background"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-black/40"></div>

        {/* Two-column layout */}
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-8 w-full px-6 md:px-16 py-12 md:py-20 items-end">
          {/* Left side: text content */}
          <div className="flex flex-col justify-end text-white text-left">
            <h2
              className="text-3xl md:text-5xl font-bold mb-6"
              style={{ fontFamily: "Nephilm" }}
            >
              Bulk Packaging Options
            </h2>
            <p
              className="text-base md:text-lg text-white/90 max-w-xl leading-8 md:leading-9 tracking-wide"
              style={{ fontFamily: "Inter18Light" }}
            >
              Solivera offers a comprehensive range of bulk packaging solutions
              designed to meet the diverse needs of international buyers. From
              barrels and totes to cargo shipments, flexitanks, IBC totes, and
              tankers, each option ensures the safe and efficient transport of
              premium Tunisian extra virgin olive oil in large quantities.
              Whether for distributors, bottling companies, or industrial
              clients, Solivera’s bulk packaging guarantees quality
              preservation, optimized logistics, and flexibility for worldwide
              delivery.
            </p>
          </div>

          {/* Right side: image fixed to bottom */}
          <div className="relative flex justify-center md:justify-end items-end mt-6 md:mt-0">
            <motion.img
              src={metal}
              alt="Metal Bulk Packaging"
              className="w-full sm:w-[80%] md:w-[70%] h-auto rounded-2xl shadow-lg object-cover"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            />
          </div>
        </div>
      </section>

      <Footer />
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: isVisible ? 1 : 0 }}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className="fixed bottom-4 sm:bottom-8 right-4 sm:right-8 p-3 sm:p-4 rounded-full bg-black/70 hover:bg-black/90 transition-all duration-300 border border-black shadow-lg z-40"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 rotate-[-90deg] text-white" />
      </motion.button>
      <ProductDetailModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={closeModal}
        onShare={handleShare}
      />
    </motion.div>
  );
};

export default Products;
